import { PlayerState } from '@brainfart/shared';

// Shown under a wrong answer: kind, quiz-night voice.
export const WRONG_ANSWER_LINES = [
  "So close. Quiz night is cruel.",
  "Not this one. Next question!",
  "Bold guess. The quizmaster says no.",
  "Wrong, but the next one is yours.",
  "Even quiz champions miss that one.",
  "Brain freeze. It happens to everyone.",
  "Big swing, small miss.",
  "The answer was hiding in plain sight.",
  "Ouch. Pub quiz pain.",
  "Respectfully, no. Keep going!",
];

export const RESULTS_LOADING_TEXTS = [
  "Marking the answer sheets... 🧠",
  "Tallying the scores...",
  "Checking with the quizmaster...",
  "Totting up the points...",
  "Crowning a quiz champion...",
  "Almost there...",
];

export function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface CommentaryInput {
  players: PlayerState[];
  lastRoundScores?: Record<string, number> | null;
  seed?: number;
}

// Deterministic — every client must compute the exact same line from the
// same synced game state, so no Math.random() here. `seed` (e.g. currentRound)
// only picks among equally-valid phrasings of the same fact for variety.
export function generateCommentary({ players, lastRoundScores, seed = 0 }: CommentaryInput): string | null {
  if (players.length === 0) return null;
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const leader = sorted[0];
  const last = sorted[sorted.length - 1];

  if (lastRoundScores) {
    const gains = Object.entries(lastRoundScores).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    if (gains.length > 0) {
      const [topId, topScore] = gains[0];
      const topPlayer = players.find(p => p.id === topId);
      if (topPlayer) {
        const phrasings = [
          `${topPlayer.name} is on fire. +${topScore} points that round. 🔥`,
          `${topPlayer.name} is pulling ahead. +${topScore} that round.`,
        ];
        return phrasings[seed % phrasings.length];
      }
    }
  }

  if (sorted.length >= 2 && sorted[0].score > 0 && Math.abs(sorted[0].score - sorted[1].score) <= 25) {
    return `${sorted[0].name} and ${sorted[1].name} are basically tied. This is getting personal.`;
  }

  if (leader && leader.score > 0) {
    const phrasings = [
      `${leader.name} is running away with this whole game. 👑`,
      `${leader.name} is currently the smartest person in this room. Enjoy it while it lasts.`,
    ];
    return phrasings[seed % phrasings.length];
  }

  if (last && sorted.length > 2 && last.score < leader.score) {
    const phrasings = [
      `${last.name} is... participating. That counts for something. 🫠`,
      `${last.name} is building real character down there in last place.`,
    ];
    return phrasings[seed % phrasings.length];
  }

  return null;
}
